import type { QueryClient } from "@tanstack/react-query"

export type CachedSessionListItem = {
  id: string
  createdAt: string
  repositoryId?: string | null
  archivedAt?: string | null
  activityHubArchiveUpdatedAt?: number | null
  [key: string]: unknown
}

type SessionListSortField = "createdAt" | "archivedAt"
type SessionListQueryKind = "active" | "archived" | "all" | "unknown"
type SessionListQueryScope = "global" | "repo" | "organization" | "picker" | "unknown"

type SessionListQueryDescriptor = {
  kind: SessionListQueryKind
  scope: SessionListQueryScope
  repositoryId: string | null
}

const DEFAULT_SESSION_LIST_RECOVERY_QUERY_KEY = "skip-recovery"
const SESSION_LIST_RECOVERY_QUERY_KEYS = new Set([DEFAULT_SESSION_LIST_RECOVERY_QUERY_KEY, "with-recovery"])
const DEFAULT_SESSION_LIST_RUNTIME_CONVERSATIONS_QUERY_KEY = "with-runtime-conversations"
const SESSION_LIST_RUNTIME_CONVERSATIONS_QUERY_KEYS = new Set([
  DEFAULT_SESSION_LIST_RUNTIME_CONVERSATIONS_QUERY_KEY,
  "skip-runtime-conversations",
])

type SessionDetailQuerySnapshot = {
  hadQuery: boolean
  data: unknown
}

type SessionListQuerySnapshot = {
  queryKey: readonly unknown[]
  descriptor: SessionListQueryDescriptor
  hadQuery: boolean
  previousSession: CachedSessionListItem | null
}

export type SessionCacheMutationSnapshot = {
  capturedAt: number
  detail: SessionDetailQuerySnapshot
  sessionLists: SessionListQuerySnapshot[]
  knownRows: CachedSessionListItem[]
}

export type AuthoritativeSessionArchiveStateUpdateResult = {
  accepted: boolean
  archivedAt: string | null
  timestamp: number
}

function serializeSessionListQueryKey(args: { queryKey: readonly unknown[] }): string {
  return JSON.stringify(args.queryKey)
}

function isSessionListRecoveryQueryKeySegment(value: unknown): boolean {
  return typeof value === "string" && SESSION_LIST_RECOVERY_QUERY_KEYS.has(value)
}

function isSessionListRuntimeConversationsQueryKeySegment(value: unknown): boolean {
  return typeof value === "string" && SESSION_LIST_RUNTIME_CONVERSATIONS_QUERY_KEYS.has(value)
}

function isSessionListDecorationQueryKeySegment(value: unknown): boolean {
  return (
    isSessionListRecoveryQueryKeySegment(value) ||
    isSessionListRuntimeConversationsQueryKeySegment(value)
  )
}

function getDecoratedSessionListQueryParts(args: {
  queryKey: readonly unknown[]
}): unknown[] {
  return args.queryKey.slice(1).filter((part) => !isSessionListDecorationQueryKeySegment(part))
}

function getSessionListQueryDescriptor(args: {
  queryKey: readonly unknown[]
}): SessionListQueryDescriptor {
  const { queryKey } = args
  if (queryKey[0] !== "sessions") {
    return { kind: "unknown", scope: "unknown", repositoryId: null }
  }

  if (queryKey.length === 1) {
    return { kind: "active", scope: "global", repositoryId: null }
  }

  if (queryKey.length === 2) {
    if (isSessionListDecorationQueryKeySegment(queryKey[1])) {
      return { kind: "active", scope: "global", repositoryId: null }
    }
    if (queryKey[1] === "archived") {
      return { kind: "archived", scope: "global", repositoryId: null }
    }
    if (queryKey[1] === "organization") {
      return { kind: "active", scope: "organization", repositoryId: null }
    }
    return {
      kind: "active",
      scope: "repo",
      repositoryId: typeof queryKey[1] === "string" ? queryKey[1] : null,
    }
  }

  if (queryKey.length === 3) {
    if (queryKey[1] === "archived" && isSessionListRecoveryQueryKeySegment(queryKey[2])) {
      return { kind: "archived", scope: "global", repositoryId: null }
    }
    if (queryKey[1] === "organization" && isSessionListRecoveryQueryKeySegment(queryKey[2])) {
      return { kind: "active", scope: "organization", repositoryId: null }
    }
    if (queryKey[1] === "organization" && queryKey[2] === "archived") {
      return { kind: "archived", scope: "organization", repositoryId: null }
    }
    if (queryKey[2] === "archived") {
      return {
        kind: "archived",
        scope: "repo",
        repositoryId: typeof queryKey[1] === "string" ? queryKey[1] : null,
      }
    }
  }

  if (
    queryKey.length === 4 &&
    queryKey[1] === "organization" &&
    queryKey[2] === "archived" &&
    isSessionListRecoveryQueryKeySegment(queryKey[3])
  ) {
    return { kind: "archived", scope: "organization", repositoryId: null }
  }

  if (queryKey.length === 4 && queryKey[2] === "picker" && queryKey[3] === "all") {
    return {
      kind: "all",
      scope: "picker",
      repositoryId: typeof queryKey[1] === "string" ? queryKey[1] : null,
    }
  }

  if (queryKey.length === 5 && queryKey[1] === "owned-repository-list") {
    return {
      kind: queryKey[4] === "archived" ? "archived" : "active",
      scope: "repo",
      repositoryId: typeof queryKey[2] === "string" ? queryKey[2] : null,
    }
  }

  const decoratedParts = getDecoratedSessionListQueryParts({ queryKey })
  if (decoratedParts.length === 0) {
    return { kind: "active", scope: "global", repositoryId: null }
  }

  if (decoratedParts.length === 1) {
    if (decoratedParts[0] === "archived") {
      return { kind: "archived", scope: "global", repositoryId: null }
    }
    if (decoratedParts[0] === "organization") {
      return { kind: "active", scope: "organization", repositoryId: null }
    }
    return {
      kind: "active",
      scope: "repo",
      repositoryId: typeof decoratedParts[0] === "string" ? decoratedParts[0] : null,
    }
  }

  if (decoratedParts.length === 2) {
    if (decoratedParts[0] === "organization" && decoratedParts[1] === "archived") {
      return { kind: "archived", scope: "organization", repositoryId: null }
    }
    if (decoratedParts[1] === "archived") {
      return {
        kind: "archived",
        scope: "repo",
        repositoryId: typeof decoratedParts[0] === "string" ? decoratedParts[0] : null,
      }
    }
  }

  if (
    decoratedParts.length === 3 &&
    decoratedParts[1] === "picker" &&
    decoratedParts[2] === "all"
  ) {
    return {
      kind: "all",
      scope: "picker",
      repositoryId: typeof decoratedParts[0] === "string" ? decoratedParts[0] : null,
    }
  }

  return { kind: "unknown", scope: "unknown", repositoryId: null }
}

function buildCanonicalSessionListQueryKeys(args: {
  repoId: string | null
}): readonly (readonly unknown[])[] {
  const queryKeys: (readonly unknown[])[] = [
    ["sessions"],
    ["sessions", "skip-recovery"],
    ["sessions", "skip-recovery", "with-runtime-conversations"],
    ["sessions", "with-recovery"],
    ["sessions", "with-recovery", "with-runtime-conversations"],
    ["sessions", "archived"],
    ["sessions", "archived", "skip-recovery"],
    ["sessions", "archived", "skip-recovery", "with-runtime-conversations"],
    ["sessions", "archived", "skip-recovery", "skip-runtime-conversations"],
    ["sessions", "archived", "with-recovery"],
    ["sessions", "archived", "with-recovery", "with-runtime-conversations"],
    ["sessions", "archived", "with-recovery", "skip-runtime-conversations"],
    ["sessions", "organization"],
    ["sessions", "organization", "skip-recovery"],
    ["sessions", "organization", "skip-recovery", "with-runtime-conversations"],
    ["sessions", "organization", "with-recovery"],
    ["sessions", "organization", "with-recovery", "with-runtime-conversations"],
    ["sessions", "organization", "archived"],
    ["sessions", "organization", "archived", "skip-recovery"],
    ["sessions", "organization", "archived", "skip-recovery", "with-runtime-conversations"],
    ["sessions", "organization", "archived", "skip-recovery", "skip-runtime-conversations"],
    ["sessions", "organization", "archived", "with-recovery"],
    ["sessions", "organization", "archived", "with-recovery", "with-runtime-conversations"],
    ["sessions", "organization", "archived", "with-recovery", "skip-runtime-conversations"],
  ]

  if (!args.repoId) {
    return queryKeys
  }

  queryKeys.push(
    ["sessions", args.repoId],
    ["sessions", args.repoId, "archived"],
    ["sessions", args.repoId, "picker", "all"],
  )
  return queryKeys
}

function dedupeSessionListQueryKeys(args: {
  queryKeys: readonly (readonly unknown[])[]
}): readonly (readonly unknown[])[] {
  const seen = new Set<string>()
  const deduped: (readonly unknown[])[] = []
  for (const queryKey of args.queryKeys) {
    const signature = serializeSessionListQueryKey({ queryKey })
    if (seen.has(signature)) continue
    seen.add(signature)
    deduped.push(queryKey)
  }
  return deduped
}

function normalizeCachedSessionListItem(args: {
  row: unknown
}): CachedSessionListItem | null {
  const { row } = args
  if (!row || typeof row !== "object") return null

  const record = row as Record<string, unknown>
  const id = typeof record.id === "string" ? record.id.trim() : ""
  if (!id) return null

  const createdAt =
    typeof record.createdAt === "string" && record.createdAt.trim().length > 0
      ? record.createdAt
      : new Date().toISOString()

  return {
    ...record,
    id,
    createdAt,
  }
}

function readCachedSessionListItem(args: {
  row: unknown
  sessionId: string
}): CachedSessionListItem | null {
  const normalized = normalizeCachedSessionListItem({ row: args.row })
  if (!normalized || normalized.id !== args.sessionId) return null
  return normalized
}

function readCachedSessionListItemFromListData(args: {
  data: unknown
  sessionId: string
}): CachedSessionListItem | null {
  const { data, sessionId } = args
  if (!Array.isArray(data)) return null

  for (const item of data) {
    const normalized = readCachedSessionListItem({ row: item, sessionId })
    if (normalized) return normalized
  }

  return null
}

function cachedSessionListDataContainsSession(args: {
  data: unknown
  sessionId: string
}): boolean {
  return Boolean(readCachedSessionListItemFromListData(args))
}

function collectKnownSessionRows(args: {
  queryClient: QueryClient
  sessionId: string
}): CachedSessionListItem[] {
  const rowsById = new Map<string, CachedSessionListItem>()

  const detailItem = normalizeCachedSessionListItem({
    row: args.queryClient.getQueryData(["session", args.sessionId]),
  })
  if (detailItem) {
    rowsById.set(detailItem.id, detailItem)
  }

  for (const [, data] of args.queryClient.getQueriesData({ queryKey: ["sessions"] })) {
    if (!Array.isArray(data)) continue

    for (const item of data) {
      const normalized = normalizeCachedSessionListItem({ row: item })
      if (!normalized) continue
      const previous = rowsById.get(normalized.id)
      rowsById.set(normalized.id, previous ? { ...previous, ...normalized } : normalized)
    }
  }

  return [...rowsById.values()]
}

function inferCachedSessionRepositoryId(args: {
  queryClient: QueryClient
  sessionId: string
}): string | null {
  const detailItem = readCachedSessionListItem({
    row: args.queryClient.getQueryData(["session", args.sessionId]),
    sessionId: args.sessionId,
  })
  if (typeof detailItem?.repositoryId === "string" && detailItem.repositoryId.trim().length > 0) {
    return detailItem.repositoryId.trim()
  }

  for (const [, data] of args.queryClient.getQueriesData({ queryKey: ["sessions"] })) {
    const listItem = readCachedSessionListItemFromListData({
      data,
      sessionId: args.sessionId,
    })
    if (
      typeof listItem?.repositoryId === "string" &&
      listItem.repositoryId.trim().length > 0
    ) {
      return listItem.repositoryId.trim()
    }
  }

  return null
}

function readCachedSessionArchiveUpdatedAtFromRow(row: Record<string, unknown>): number {
  const archiveUpdatedAt = row.activityHubArchiveUpdatedAt
  if (
    typeof archiveUpdatedAt === "number" &&
    Number.isFinite(archiveUpdatedAt) &&
    archiveUpdatedAt > 0
  ) {
    return Math.floor(archiveUpdatedAt)
  }

  return 0
}

export function readSessionArchiveUpdatedAtFromCaches(args: {
  queryClient: QueryClient
  sessionId: string
}): number {
  const { queryClient, sessionId } = args

  const readTimestamp = (row: unknown): number => {
    if (!row || typeof row !== "object") return 0
    return readCachedSessionArchiveUpdatedAtFromRow(row as Record<string, unknown>)
  }

  const detailTimestamp = readTimestamp(queryClient.getQueryData(["session", sessionId]))
  let maxTimestamp = detailTimestamp

  const sessionLists = queryClient.getQueriesData({ queryKey: ["sessions"] })
  for (const [, list] of sessionLists) {
    if (!Array.isArray(list)) continue

    for (const item of list) {
      if (!item || typeof item !== "object") continue
      const row = item as Record<string, unknown>
      if (row.id !== sessionId) continue

      const timestamp = readCachedSessionArchiveUpdatedAtFromRow(row)
      if (timestamp > maxTimestamp) {
        maxTimestamp = timestamp
      }
    }
  }

  return maxTimestamp
}

export function restoreSessionInCache(
  old: CachedSessionListItem[] | undefined,
  restored: CachedSessionListItem,
): CachedSessionListItem[] {
  const next = [...(old ?? [])]
  const existingIndex = next.findIndex((session) => session.id === restored.id)
  if (existingIndex >= 0) {
    next[existingIndex] = {
      ...next[existingIndex],
      ...restored,
    }
  } else {
    next.push(restored)
  }
  next.sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
  return next
}

export function upsertSessionInCache(args: {
  old: CachedSessionListItem[] | undefined
  item: CachedSessionListItem
  sortField?: SessionListSortField
}): CachedSessionListItem[] {
  const { old, item, sortField = "createdAt" } = args
  const next = [...(old ?? [])]
  const existingIndex = next.findIndex((session) => session.id === item.id)
  if (existingIndex >= 0) {
    next[existingIndex] = {
      ...next[existingIndex],
      ...item,
    }
  } else {
    next.push(item)
  }
  next.sort((left, right) => {
    const leftValue = sortField === "archivedAt"
      ? (left.archivedAt ?? left.createdAt)
      : left.createdAt
    const rightValue = sortField === "archivedAt"
      ? (right.archivedAt ?? right.createdAt)
      : right.createdAt
    return new Date(rightValue).getTime() - new Date(leftValue).getTime()
  })
  return next
}

export function captureSessionCacheMutationSnapshot(args: {
  queryClient: QueryClient
  sessionId: string
  repoId?: string | null
}): SessionCacheMutationSnapshot {
  const { queryClient, sessionId } = args
  const existingQueryKeys = queryClient
    .getQueriesData({ queryKey: ["sessions"] })
    .map(([queryKey]) => queryKey as readonly unknown[])
  const effectiveRepoId =
    typeof args.repoId === "string" && args.repoId.trim().length > 0
      ? args.repoId.trim()
      : inferCachedSessionRepositoryId({ queryClient, sessionId })
  const sessionListQueryKeys = dedupeSessionListQueryKeys({
    queryKeys: [
      ...buildCanonicalSessionListQueryKeys({ repoId: effectiveRepoId }),
      ...existingQueryKeys,
    ],
  })

  return {
    capturedAt: Date.now(),
    detail: {
      hadQuery: queryClient.getQueryState(["session", sessionId]) !== undefined,
      data: queryClient.getQueryData(["session", sessionId]),
    },
    sessionLists: sessionListQueryKeys.map((queryKey) => ({
      queryKey,
      descriptor: getSessionListQueryDescriptor({ queryKey }),
      hadQuery: queryClient.getQueryState(queryKey) !== undefined,
      previousSession: readCachedSessionListItemFromListData({
        data: queryClient.getQueryData(queryKey),
        sessionId,
      }),
    })),
    knownRows: collectKnownSessionRows({ queryClient, sessionId }),
  }
}

export function restoreSessionCacheMutationSnapshot(args: {
  queryClient: QueryClient
  sessionId: string
  snapshot: SessionCacheMutationSnapshot
}): void {
  const { queryClient, sessionId, snapshot } = args

  if (!snapshot.detail.hadQuery) {
    const currentDetailState = queryClient.getQueryState(["session", sessionId])
    const currentDetail = readCachedSessionListItem({
      row: queryClient.getQueryData(["session", sessionId]),
      sessionId,
    })
    if (currentDetail || (currentDetailState && currentDetailState.dataUpdatedAt >= snapshot.capturedAt)) {
      queryClient.removeQueries({ queryKey: ["session", sessionId], exact: true })
    }
  } else {
    queryClient.setQueryData(["session", sessionId], snapshot.detail.data)
  }

  for (const entry of snapshot.sessionLists) {
    const sortField: SessionListSortField =
      entry.descriptor.kind === "archived" ? "archivedAt" : "createdAt"

    if (!entry.hadQuery) {
      const currentQueryState = queryClient.getQueryState(entry.queryKey)
      if (
        cachedSessionListDataContainsSession({
          data: queryClient.getQueryData(entry.queryKey),
          sessionId,
        }) ||
        (currentQueryState && currentQueryState.dataUpdatedAt >= snapshot.capturedAt)
      ) {
        queryClient.removeQueries({ queryKey: entry.queryKey, exact: true })
      }
      continue
    }

    queryClient.setQueryData<CachedSessionListItem[]>(entry.queryKey, (old) => {
      const filtered = Array.isArray(old)
        ? old.filter((item) => item.id !== sessionId)
        : []
      if (!entry.previousSession) {
        return filtered
      }
      return upsertSessionInCache({
        old: filtered,
        item: entry.previousSession,
        sortField,
      })
    })
  }
}

export function selectSessionCacheMutationSource(args: {
  snapshot: SessionCacheMutationSnapshot
  sessionId: string
}): CachedSessionListItem | null {
  const { snapshot, sessionId } = args

  const detailItem = readCachedSessionListItem({
    row: snapshot.detail.data,
    sessionId,
  })
  if (detailItem) return detailItem

  for (const item of snapshot.knownRows) {
    if (item.id === sessionId) return item
  }

  return null
}

function matchesSessionListDescriptor(args: {
  descriptor: SessionListQueryDescriptor
  row: CachedSessionListItem
}): boolean {
  const { descriptor, row } = args
  if (descriptor.kind === "unknown") return false

  /**
   * Organization (team) sessions should only appear in organization-scoped
   * session lists. Without this guard, seeded caches can leak team sessions
   * into user-owned lists until an API refetch replaces them.
   */
  if (descriptor.scope !== "organization" && row.organizationThread === true) {
    return false
  }

  if (descriptor.scope === "organization" && row.organizationThread !== true) {
    return false
  }

  if (
    (descriptor.scope === "repo" || descriptor.scope === "picker") &&
    descriptor.repositoryId !== row.repositoryId
  ) {
    return false
  }

  if (descriptor.kind === "active") {
    return !row.archivedAt
  }

  if (descriptor.kind === "archived") {
    return Boolean(row.archivedAt)
  }

  return true
}

function buildSeededSessionListCache(args: {
  snapshot: SessionCacheMutationSnapshot
  descriptor: SessionListQueryDescriptor
  sessionId: string
  item: CachedSessionListItem | null
}): CachedSessionListItem[] {
  const rowsById = new Map<string, CachedSessionListItem>()

  for (const row of args.snapshot.knownRows) {
    rowsById.set(row.id, row)
  }

  if (args.item) {
    rowsById.set(args.sessionId, args.item)
  } else {
    rowsById.delete(args.sessionId)
  }

  const sortField: SessionListSortField =
    args.descriptor.kind === "archived" ? "archivedAt" : "createdAt"
  return [...rowsById.values()]
    .filter((row) => matchesSessionListDescriptor({ descriptor: args.descriptor, row }))
    .sort((left, right) => {
      const leftValue = sortField === "archivedAt"
        ? (left.archivedAt ?? left.createdAt)
        : left.createdAt
      const rightValue = sortField === "archivedAt"
        ? (right.archivedAt ?? right.createdAt)
        : right.createdAt
      return new Date(rightValue).getTime() - new Date(leftValue).getTime()
    })
}

export function applyOptimisticSessionMembershipToSessionListCaches(args: {
  queryClient: QueryClient
  snapshot: SessionCacheMutationSnapshot
  sessionId: string
  item: CachedSessionListItem | null
}): void {
  const { queryClient, snapshot, sessionId, item } = args
  for (const entry of snapshot.sessionLists) {
    if (entry.descriptor.kind === "unknown") {
      continue
    }

    const sortField: SessionListSortField =
      entry.descriptor.kind === "archived" ? "archivedAt" : "createdAt"
    queryClient.setQueryData<CachedSessionListItem[]>(entry.queryKey, (old) => {
      const base = Array.isArray(old)
        ? old
        : buildSeededSessionListCache({
            snapshot,
            descriptor: entry.descriptor,
            sessionId,
            item,
          })
      const filtered = base.filter((session) => session.id !== sessionId)
      if (!item || !matchesSessionListDescriptor({ descriptor: entry.descriptor, row: item })) {
        return filtered
      }
      return upsertSessionInCache({
        old: filtered,
        item,
        sortField,
      })
    })
  }
}

export function applyAuthoritativeSessionArchiveStateUpdate(args: {
  queryClient: QueryClient
  sessionId: string
  archivedAt: string | null
  timestamp: number | null
  item?: CachedSessionListItem | null
  repoId?: string | null
}): AuthoritativeSessionArchiveStateUpdateResult {
  const nextTimestamp =
    typeof args.timestamp === "number" && Number.isFinite(args.timestamp) && args.timestamp > 0
      ? Math.floor(args.timestamp)
      : Date.now()
  const lastKnownTimestamp = readSessionArchiveUpdatedAtFromCaches({
    queryClient: args.queryClient,
    sessionId: args.sessionId,
  })
  if (nextTimestamp < lastKnownTimestamp) {
    return {
      accepted: false,
      archivedAt: args.archivedAt,
      timestamp: nextTimestamp,
    }
  }

  const snapshot = captureSessionCacheMutationSnapshot({
    queryClient: args.queryClient,
    sessionId: args.sessionId,
    repoId: args.repoId,
  })
  const sourceItem = selectSessionCacheMutationSource({
    snapshot,
    sessionId: args.sessionId,
  })
  const normalizedPayloadItem = args.item
    ? normalizeCachedSessionListItem({ row: args.item })
    : null
  const effectiveItem = normalizedPayloadItem ?? sourceItem

  args.queryClient.setQueryData(["session", args.sessionId], (old: unknown) => {
    if (!old || typeof old !== "object") {
      if (!effectiveItem) return old
      return {
        ...effectiveItem,
        archivedAt: args.archivedAt,
        activityHubArchiveUpdatedAt: nextTimestamp,
      }
    }

    return {
      ...(old as Record<string, unknown>),
      archivedAt: args.archivedAt,
      activityHubArchiveUpdatedAt: nextTimestamp,
    }
  })

  if (effectiveItem) {
    applyOptimisticSessionMembershipToSessionListCaches({
      queryClient: args.queryClient,
      snapshot,
      sessionId: args.sessionId,
      item: {
        ...effectiveItem,
        archivedAt: args.archivedAt,
        activityHubArchiveUpdatedAt: nextTimestamp,
      },
    })
  }

  return {
    accepted: true,
    archivedAt: args.archivedAt,
    timestamp: nextTimestamp,
  }
}
